import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import * as R from 'ramda';
import { ReactNode } from 'react';
import { GroupedVirtuoso } from 'react-virtuoso';
import { useShallow } from 'zustand/react/shallow';
import LangEnum from '../../../lang/LangEnum';
import { useGetProductColorsSelloutTotals } from '../../../services/modules/product-color-sellout/product-color-sellout.queries';
import { mapSelloutProductColorsTotals } from '../../../services/modules/product-color-sellout/productColorSellout.utils';
import { SuggestedProductsToCustomer } from '../../../services/modules/products/products.types';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import useProductDetailsStore from '../../../stores/ProductDetails/productDetails.store';
import { UsedInOfflineMode } from '../../offline/types/UsedInOfflineMode';
import useOrderBook from '../../order-book/hooks/useOrderBook';
import { GridStoreProvider } from '../../orders/products/product/grid/store/GridStoreProvider';
import { useTranslation } from '../../translation/TranslationContext';
import { ESProductSearchState } from '../details/productDetails.state';
import ProductDetailsSectionHeader from './ProductDetailsSectionHeader';
import { ProductDetailsOrderDataProvider } from './contexts/ProductDetailsOrderData.context';
import useProductDetailsList from './hooks/useProductDetailsList';
import useProductDetailsRefetch from './hooks/useProductDetailsRefetch';
import useProductDetailsActions from './hooks/useProductsDetailsActions';
import productDetailsListComponents from './utils/productDetailsList.components';
import ProductDetailsCombination from './views/desktop/ProductDetailsCombination';

interface IProductDetailsList extends UsedInOfflineMode {
  product: ESProductSearchState;
  disableGrid?: boolean;
  scrollParent?: HTMLElement | null;
  footer?: ReactNode;
  suggestedProducts?: SuggestedProductsToCustomer[];
}

const ProductDetailsList = ({
  footer,
  product,
  disableGrid,
  scrollParent,
  suggestedProducts,
  isOfflineMode = false,
}: IProductDetailsList) => {
  const { translate } = useTranslation();
  const selectedOrder = useOrderBookStore((state) => state.selectedOrder);

  const { allOrders } = useOrderBook();

  const { refetchUsingStack, refetchUsingOrderIds } = useProductDetailsRefetch(product.productId);

  const { getFilteredOrder, getIsSuggestedProduct } = useProductDetailsActions();
  const {
    combinations,
    combinationsLoading,
    countLoading,
    fetchNextPage,
    groupCounts,
    getCombinationErrors,
  } = useProductDetailsList({ product, isOfflineMode });
  const { productGridParams } = useProductDetailsStore(
    useShallow((state) => ({
      productGridParams: state.productGridParams,
    })),
  );

  const { data: fetchedSelloutData, isFetching: isFetchingSellout } =
    useGetProductColorsSelloutTotals({
      productColorIds: product.allColors.map((color) => color.productColorId),
      orderId: productGridParams.isFinishedOrder
        ? productGridParams.orderIds?.[0]
        : selectedOrder?.id,
    });
  const selloutTotalsMap = mapSelloutProductColorsTotals(fetchedSelloutData ?? []);

  if (combinationsLoading || countLoading || !combinations) {
    return null;
  }

  if (R.isEmpty(combinations)) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingBlock: 5,
        }}
      >
        <Typography variant="body2" color="text.disabled">
          {translate(LangEnum.PACK_NO_GRIDS_AVAILABLE)}
        </Typography>
      </Box>
    );
  }

  const orderIds = [...new Set(combinations.map((combination) => combination.orderId))];

  return (
    <GridStoreProvider onAfterSave={refetchUsingStack}>
      <ProductDetailsOrderDataProvider productId={product.productId} orderIds={orderIds}>
        <GroupedVirtuoso
          style={{
            width: '100%',
            height: '100%',
          }}
          tabIndex={-1}
          groupCounts={groupCounts}
          groupContent={(groupIndex) => (
            <ProductDetailsSectionHeader product={product} order={getFilteredOrder(groupIndex)} />
          )}
          customScrollParent={scrollParent ?? undefined}
          increaseViewportBy={{ top: 66, bottom: 147 }}
          components={{
            ...productDetailsListComponents,
            Footer: (props) => <div {...props}>{footer}</div>,
          }}
          endReached={() => {
            if (!combinations.length) {
              return;
            }

            fetchNextPage();
          }}
          itemContent={(currentRowIndex) => {
            const combination = combinations[currentRowIndex];

            if (!combination) {
              return null;
            }

            return (
              <ProductDetailsCombination
                selectedOrder={selectedOrder}
                allOrders={allOrders}
                combination={combination}
                disableGrid={disableGrid}
                refetchUsingOrderIds={refetchUsingOrderIds}
                isOfflineMode={isOfflineMode}
                suggestedProducts={suggestedProducts}
                getIsSuggestedProduct={getIsSuggestedProduct}
                selloutQuantitySold={selloutTotalsMap.get(combination.productColorId)}
                isFetchingSellout={isFetchingSellout}
                validationErrors={getCombinationErrors(combination)}
              />
            );
          }}
        />
      </ProductDetailsOrderDataProvider>
    </GridStoreProvider>
  );
};

export default ProductDetailsList;
